import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./Tooltip.scss";

const GAP = 8;
const VIEWPORT_MARGIN = 8;
const ARROW_EDGE_MARGIN = 14;

/**
 * Generic hover tooltip. Wraps `children` in a positioned anchor and shows
 * `content` in a floating label above/below on hover — auto-picks whichever
 * side has more room unless `position` is forced.
 *
 * The floating element is portaled to `document.body` and positioned with
 * `position: fixed` from the anchor's own `getBoundingClientRect()` - not
 * rendered in place with `position: absolute` - so it isn't clipped by a
 * scrolling/`overflow: hidden` ancestor (e.g. a modal body). Dismisses on
 * scroll rather than re-tracking the anchor, since a fixed-position tooltip
 * would otherwise visually detach from it mid-scroll.
 *
 * `position` is a PREFERENCE, not a guarantee: an explicit "top"/"bottom"
 * still gets flipped to the other side if the tooltip's real (measured)
 * size wouldn't fit there but does fit on the other side - e.g. a button
 * sitting right under a fixed header has ~0px of room above it, so a
 * forced `position="top"` tooltip would otherwise render off the top edge
 * of the viewport with its content invisible above y=0. The initial
 * position (set on hover, before the tooltip has ever rendered so its real
 * size isn't knowable yet) is a same-frame `useLayoutEffect` correction
 * once it has - which runs before the browser paints, so there's no visible
 * jump between the guess and the corrected position. Horizontal position is
 * clamped the same way so the box can't run off the left/right edges
 * either, independent of which side it's on.
 *
 * The floating element is classed `.ui-tooltip`, not the more obvious
 * `.tooltip` — this codebase already has an unrelated global `.tooltip`
 * class (src/assets/scss/components/_tooltip.scss, forwarded app-wide via
 * main.scss) that sets `position: relative`. A same-specificity class-name
 * collision with that silently overrode this component's `position:
 * fixed`, breaking every offset/transform that depends on it - the
 * tooltip existed in the DOM but rendered invisibly off-position. Don't
 * rename this back to `.tooltip` without also fully retiring that old file.
 *
 * Usage: <Tooltip content="Companies" disabled={!mini}><Icon /></Tooltip>
 */
const Tooltip = ({ content, children, position = "auto", disabled = false, className = "" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [resolvedPosition, setResolvedPosition] = useState(
    position === "auto" ? "top" : position
  );
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [arrowLeft, setArrowLeft] = useState("50%");
  const anchorRef = useRef(null);
  const tooltipRef = useRef(null);

  const active = !disabled && Boolean(content);

  const handleMouseEnter = () => {
    if (!active || !anchorRef.current) return;

    const rect = anchorRef.current.getBoundingClientRect();
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    const preferred = position === "auto" ? (spaceAbove >= spaceBelow ? "top" : "bottom") : position;

    // Rough first guess (real size isn't known until it's rendered) -
    // corrected below via useLayoutEffect before this ever paints.
    setResolvedPosition(preferred);
    setCoords({
      top: preferred === "top" ? rect.top - GAP : rect.bottom + GAP,
      left: rect.left + rect.width / 2,
    });
    setIsVisible(true);
  };

  const handleMouseLeave = () => setIsVisible(false);

  useEffect(() => {
    if (!isVisible) return;
    window.addEventListener("scroll", handleMouseLeave, true);
    return () => window.removeEventListener("scroll", handleMouseLeave, true);
  }, [isVisible]);

  useLayoutEffect(() => {
    if (!isVisible || !anchorRef.current || !tooltipRef.current) return;

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const tipRect = tooltipRef.current.getBoundingClientRect();
    const spaceAbove = anchorRect.top;
    const spaceBelow = window.innerHeight - anchorRect.bottom;
    const needed = tipRect.height + GAP;

    let finalPosition = resolvedPosition;
    if (finalPosition === "top" && needed > spaceAbove && spaceBelow >= spaceAbove) {
      finalPosition = "bottom";
    } else if (finalPosition === "bottom" && needed > spaceBelow && spaceAbove > spaceBelow) {
      finalPosition = "top";
    }

    const top = finalPosition === "top" ? anchorRect.top - GAP : anchorRect.bottom + GAP;

    const halfWidth = tipRect.width / 2;
    const anchorCenter = anchorRect.left + anchorRect.width / 2;
    const left = Math.min(
      Math.max(anchorCenter, halfWidth + VIEWPORT_MARGIN),
      window.innerWidth - halfWidth - VIEWPORT_MARGIN
    );

    // The arrow points at the anchor's actual center, not the box's own
    // center - once `left` above gets clamped away from the anchor (box
    // pinned near a viewport edge while the anchor sits further toward
    // that edge), a `left: 50%` arrow would silently stop pointing at
    // what it's meant to explain. Clamped inward from the box's own edges
    // so the arrow triangle never renders past a rounded corner.
    const arrowOffsetInBox = anchorCenter - left + halfWidth;
    const clampedArrow = Math.min(
      Math.max(arrowOffsetInBox, ARROW_EDGE_MARGIN),
      tipRect.width - ARROW_EDGE_MARGIN
    );

    if (finalPosition !== resolvedPosition) setResolvedPosition(finalPosition);
    if (top !== coords.top || left !== coords.left) setCoords({ top, left });
    setArrowLeft(`${clampedArrow}px`);
    // Re-measuring only depends on the tooltip becoming visible - re-running
    // this on every coords/resolvedPosition change it produces itself would
    // loop; it already reads their latest values via the closure each time
    // isVisible flips true.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  return (
    <div
      ref={anchorRef}
      className={`tooltip-anchor ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {active &&
        isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            className={`ui-tooltip ui-tooltip--${resolvedPosition}`}
            role="tooltip"
            style={{ top: coords.top, left: coords.left, "--arrow-left": arrowLeft }}
          >
            {content}
          </div>,
          document.body
        )}
    </div>
  );
};

export default Tooltip;
